/**
 * Pod messages inbox: thread list plus reader, across every pod the viewer belongs to.
 *
 * Mirrors the direct-messages layout (list beside thread on desktop, one at a time on mobile) so
 * both tabs of the Messages page behave the same way. Tutors additionally get the compose entry
 * point here, which is what makes pod messaging reachable without going to the pod page.
 */
import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import podMessagingService from '../../services/podMessagingService'
import PodThreadList from './PodThreadList'
import PodThreadView from './PodThreadView'
import PodComposeModal from './PodComposeModal'

export default function PodInbox({ onUnreadChange }) {
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [activeThread, setActiveThread] = useState(null)
  const [threadLoading, setThreadLoading] = useState(false)
  const [composing, setComposing] = useState(false)

  const loadThreads = useCallback(async () => {
    try {
      const list = await podMessagingService.listThreads()
      setThreads(list)
      onUnreadChange?.(list.reduce((sum, t) => sum + (t.unreadCount || 0), 0))
    } catch (err) {
      // A 403 just means pods are off for this account; that is not an error worth shouting about.
      if (err?.response?.status !== 403) toast.error('Could not load pod messages.')
      setThreads([])
    } finally {
      setLoading(false)
    }
  }, [onUnreadChange])

  useEffect(() => {
    loadThreads()
  }, [loadThreads])

  const openThread = useCallback(
    async (threadId) => {
      setSelectedId(threadId)
      setThreadLoading(true)
      try {
        const thread = await podMessagingService.getThread(threadId)
        setActiveThread(thread)
        // Marking read is best-effort: failing to clear a badge must not block reading.
        await podMessagingService.markRead(threadId).catch(() => {})
        await loadThreads()
      } catch (err) {
        toast.error(err?.response?.data?.error?.message || 'Could not open the conversation.')
        setSelectedId(null)
      } finally {
        setThreadLoading(false)
      }
    },
    [loadThreads]
  )

  // Pods this viewer can compose in: tutor-role threads reveal which pods they tutor. A tutor with
  // no threads yet still needs the compose option, so fall back to any pod seen in the list.
  const tutorPods = []
  const seen = new Set()
  for (const t of threads) {
    if (t.myRole === 'tutor' && !seen.has(t.organizationId)) {
      seen.add(t.organizationId)
      tutorPods.push({ id: t.organizationId, name: t.organizationName })
    }
  }
  const canCompose = tutorPods.length > 0

  return (
    <div className="flex h-full">
      <div
        className={`w-full flex-shrink-0 md:w-80 lg:w-96 ${selectedId ? 'hidden md:block' : 'block'}`}
      >
        <PodThreadList
          threads={threads}
          loading={loading}
          selectedThreadId={selectedId}
          onSelectThread={openThread}
          onCompose={() => setComposing(true)}
          canCompose={canCompose}
        />
      </div>

      <div className={`flex-1 ${selectedId ? 'block' : 'hidden md:block'}`}>
        <PodThreadView
          thread={activeThread}
          loading={threadLoading}
          onBack={() => {
            setSelectedId(null)
            setActiveThread(null)
          }}
          onReplied={() => openThread(selectedId)}
        />
      </div>

      <PodComposeModal
        isOpen={composing}
        pods={tutorPods}
        onClose={() => setComposing(false)}
        onSent={() => {
          setComposing(false)
          loadThreads()
        }}
      />
    </div>
  )
}
